import { useEffect, useMemo, type ReactNode } from 'react';
import clsx from 'clsx';
import { KandoCenterIcon } from './KandoCenterIcon';
import { UkropTip } from '../tooltips/DiagramTooltipContext';

export type KandoItem = {
  id: string;
  label: string;
  icon?: ReactNode;
  disabled?: boolean;
  danger?: boolean;
};

export function KandoMenu({
  x,
  y,
  items,
  onSelect,
  onClose,
}: {
  x: number;
  y: number;
  items: KandoItem[];
  onSelect: (id: string) => void;
  onClose: () => void;
}) {
  const placed = useMemo(() => {
    const n = Math.max(items.length, 1);
    const radius = 86;
    return items.map((item, i) => {
      const angle = (-90 + (360 / n) * i) * (Math.PI / 180);
      return {
        item,
        left: Math.cos(angle) * radius,
        top: Math.sin(angle) * radius,
      };
    });
  }, [items]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="kando-root" role="menu" aria-label="Контекстне меню">
      <button type="button" className="kando-backdrop" aria-label="Закрити" onClick={onClose} />
      <div className="kando-panel" style={{ left: x, top: y }}>
        <div className="kando-ring">
          {placed.map(({ item, left, top }) => (
            <UkropTip key={item.id} text={item.label}>
              <button
                type="button"
                role="menuitem"
                disabled={item.disabled}
                className={clsx(
                  'kando-item',
                  item.danger && 'is-danger',
                  item.disabled && 'is-disabled',
                )}
                style={{ transform: `translate(${left}px, ${top}px)` }}
                onClick={() => {
                  if (item.disabled) return;
                  onSelect(item.id);
                }}
              >
                {item.icon ? <span className="kando-item-icon">{item.icon}</span> : null}
                <span className="kando-item-label">{item.label}</span>
              </button>
            </UkropTip>
          ))}
          <UkropTip text="Закрити">
            <button
              type="button"
              className="kando-center"
              onClick={onClose}
              aria-label="Закрити меню"
            >
              <KandoCenterIcon size={30} />
            </button>
          </UkropTip>
        </div>
      </div>
    </div>
  );
}
